import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { FaFilter } from 'react-icons/fa';
import './GraveManagement.css';
import './UserModal.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function GraveManagement() {
  const { token, role } = useContext(AuthContext);
  const [gardens, setGardens] = useState({});
  const [selectedGarden, setSelectedGarden] = useState('A');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unifiedLots, setUnifiedLots] = useState([]);
  const [filteredGraves, setFilteredGraves] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedGrave, setSelectedGrave] = useState(null);
  const [editForm, setEditForm] = useState({
    id: '',
    name: '',
    birth: '',
    death: '',
    status: 'available',
    row: '',
    column: '',
    garden: ''
  });

  useEffect(() => {
    if (token && role === 'admin') {
      fetchGardenConfig();
      fetchUnifiedLots();
    } else if (!token) {
      setError('Please log in to access grave management');
      setLoading(false);
    } else if (role !== 'admin') {
      setError('Admin access required for grave management');
      setLoading(false);
    }
  }, [token, role]);

  useEffect(() => {
    // Filter graves for selected garden
    const graves = unifiedLots.filter(lot => 
      lot.type === 'grave' && 
      lot.garden && 
      lot.garden.toUpperCase() === selectedGarden.toUpperCase()
    );
    setFilteredGraves(graves);
  }, [unifiedLots, selectedGarden]);

  const fetchGardenConfig = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/public/garden-config`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGardens(response.data);
    } catch (error) {
      console.error('Error fetching garden config:', error);
      setError('Failed to load garden configuration');
      
      // Fallback to hardcoded config if API fails
      const fallbackConfig = {
        A: { rows: 20, columns: 42 },
        B: { rows: 20, columns: 61 },
        C: { rows: 20, columns: 42 },
        D: { rows: 20, columns: 50 }
      };
      setGardens(fallbackConfig);
    }
  };

  const fetchUnifiedLots = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/public/unified-lots`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const dbGraves = response.data.map(grave => ({
        id: grave.id, // Use the custom ID from unifiedLots (e.g., "A-1-1")
        _id: grave._id, // Keep the MongoDB _id for reference
        name: grave.name || '',
        sqm: grave.sqm || 2, // Default to 2
        price: grave.price || 50000, // Default to 50000
        birth: grave.birth || '',
        death: grave.death || '',
        status: grave.status || 'available',
        row: grave.row || 1,
        column: grave.column || 1,
        garden: grave.garden || 'A',
        type: 'grave'
      }));
      
      setUnifiedLots(dbGraves);
    } catch (error) {
      console.error('Error fetching unified lots:', error);
      setError('Failed to load grave data');
    } finally {
      setLoading(false);
    }
  };

  const handleGardenSelect = (garden) => {
    setSelectedGarden(garden);
  };

  const handleGraveEdit = (grave) => {
    setSelectedGrave(grave);
    setEditForm({
      id: grave.id || '', // Lot ID
      name: grave.name || '', // Name
      sqm: grave.sqm || 2, // Square Meters (default to 2)
      price: grave.price || 50000, // Price (default to 50000)
      birth: grave.birth || '', // Birth
      death: grave.death || '', // Death
      status: grave.status || 'available', // Status
      row: grave.row || 1,
      column: grave.column || 1,
      garden: grave.garden || 'A'
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => {
      const newForm = {
        ...prev,
        [field]: value
      };
      
      // Auto-calculate price when square meters changes
      if (field === 'sqm' && value) {
        // Assuming price per square meter is 25000 (you can adjust this)
        const pricePerSqm = 25000;
        newForm.price = (parseFloat(value) * pricePerSqm).toFixed(2);
      }
      
      return newForm;
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      // Use the original grave ID for the API call, but send the updated data
      const updateData = {
        id: editForm.id, // Lot ID
        name: editForm.name, // Name
        sqm: editForm.sqm, // Square Meters
        price: editForm.price, // Price
        birth: editForm.birth, // Birth
        death: editForm.death, // Death
        status: editForm.status // Status
        // Remove row, column, garden since they're permanent
      };
      
      const response = await axios.put(
        `${API_BASE_URL}/admin/graves/${selectedGrave.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setUnifiedLots(prev => prev.map(grave => 
        grave.id === selectedGrave.id ? { 
          ...grave, 
          id: editForm.id, // Lot ID
          name: editForm.name, // Name
          sqm: editForm.sqm, // Square Meters
          price: editForm.price, // Price
          birth: editForm.birth, // Birth
          death: editForm.death, // Death
          status: editForm.status // Status
        } : grave
      ));
      
      setShowEditModal(false);
      setSelectedGrave(null);
    } catch (error) {
      console.error('Error updating grave:', error);
      setError('Failed to update grave');
    }
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedGrave(null);
    setEditForm({
      id: '',
      name: '',
      sqm: '',
      price: '',
      birth: '',
      death: '',
      status: '',
      row: '',
      column: '',
      garden: ''
    });
  };

  const handleGraveStatusChange = async (graveId, newStatus) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/admin/graves/${graveId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setUnifiedLots(prev => prev.map(grave => 
        grave.id === graveId ? { ...grave, status: newStatus } : grave
      ));
    } catch (error) {
      console.error('Error updating grave status:', error);
      setError('Failed to update grave status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      available: '#34c759',
      occupied: '#ff3b30',  // Red for occupied
      reserved: '#007bff',
      maintenance: '#ff9500',
      unavailable: '#ffcc00'  // Yellow for unavailable
    };
    return colors[status] || '#8e8e93';
  };

  // Grave Grid Component - defined inside main component
  const GraveGrid = ({ garden, graves, onStatusChange, onGraveEdit, loading }) => {
    const [selectedGraves, setSelectedGraves] = useState(new Set());

    const getStatusColor = (status) => {
      const colors = {
        available: '#34c759',
        occupied: '#ff3b30',  // Red for occupied
        reserved: '#007bff',
        maintenance: '#ff9500',
        unavailable: '#ffcc00'  // Yellow for unavailable
      };
      return colors[status] || '#8e8e93';
    };

    // Create a map of graves by position
    const graveMap = {};
    graves.forEach(grave => {
      if (grave.row && grave.column) {
        graveMap[`${grave.row}-${grave.column}`] = grave;
      }
    });

    const handleGraveClick = (row, column) => {
      const grave = graveMap[`${row}-${column}`];
      
      if (grave) {
        // Open edit modal for the grave
        onGraveEdit(grave);
      }
    };

    const handleGraveSelect = (row, column) => {
      const key = `${row}-${column}`;
      setSelectedGraves(prev => {
        const newSet = new Set(prev);
        if (newSet.has(key)) {
          newSet.delete(key);
        } else {
          newSet.add(key);
        }
        return newSet;
      });
    };

    // Define columns per garden
    const getColumnsPerGarden = (garden) => {
      switch (garden.toUpperCase()) {
        case 'A': return 42;
        case 'B': return 61; // 63 - 3 + 1 = 61 columns (3 to 63)
        case 'C': return 42; // 43 - 2 + 1 = 42 columns (2 to 43)
        case 'D': return 50; // 53 - 4 + 1 = 50 columns (4 to 53)
        default: return 42;
      }
    };

    // For Garden B, C, and D, filter to specific ranges
    // For other gardens, use horizontal flow layout
    let sortedGraves, gravesPerRow, config;
    
    if (garden.toUpperCase() === 'B') {
      // Garden B: Use specific row/column ranges
      config = { startColumn: 3, endColumn: 63, startRow: 2, endRow: 20 };
      const gardenGraves = graves.filter(grave => 
        grave.row >= config.startRow && grave.row <= config.endRow &&
        grave.column >= config.startColumn && grave.column <= config.endColumn
      );
      
      // Group by row
      const gravesByRow = {};
      gardenGraves.forEach(grave => {
        if (!gravesByRow[grave.row]) {
          gravesByRow[grave.row] = [];
        }
        gravesByRow[grave.row].push(grave);
      });
      
      // Sort rows and columns within each row
      sortedGraves = Object.keys(gravesByRow)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map(row => gravesByRow[row].sort((a, b) => a.column - b.column));
      
      gravesPerRow = config.endColumn - config.startColumn + 1;
    } else if (garden.toUpperCase() === 'C') {
      // Garden C: Use specific row/column ranges
      config = { startColumn: 2, endColumn: 43, startRow: 2, endRow: 20 };
      const gardenGraves = graves.filter(grave => 
        grave.row >= config.startRow && grave.row <= config.endRow &&
        grave.column >= config.startColumn && grave.column <= config.endColumn
      );
      
      // Group by row
      const gravesByRow = {};
      gardenGraves.forEach(grave => {
        if (!gravesByRow[grave.row]) {
          gravesByRow[grave.row] = [];
        }
        gravesByRow[grave.row].push(grave);
      });
      
      // Sort rows and columns within each row
      sortedGraves = Object.keys(gravesByRow)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map(row => gravesByRow[row].sort((a, b) => a.column - b.column));
      
      gravesPerRow = config.endColumn - config.startColumn + 1;
    } else if (garden.toUpperCase() === 'D') {
      // Garden D: Use specific row/column ranges
      config = { startColumn: 4, endColumn: 53, startRow: 2, endRow: 20 };
      const gardenGraves = graves.filter(grave => 
        grave.row >= config.startRow && grave.row <= config.endRow &&
        grave.column >= config.startColumn && grave.column <= config.endColumn
      );
      
      // Group by row
      const gravesByRow = {};
      gardenGraves.forEach(grave => {
        if (!gravesByRow[grave.row]) {
          gravesByRow[grave.row] = [];
        }
        gravesByRow[grave.row].push(grave);
      });
      
      // Sort rows and columns within each row
      sortedGraves = Object.keys(gravesByRow)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map(row => gravesByRow[row].sort((a, b) => a.column - b.column));
      
      gravesPerRow = config.endColumn - config.startColumn + 1;
    } else {
      // Garden A and others: Use horizontal flow layout
      gravesPerRow = getColumnsPerGarden(garden);
      const rows = [];
      for (let i = 0; i < graves.length; i += gravesPerRow) {
        rows.push(graves.slice(i, i + gravesPerRow));
      }
      sortedGraves = rows;
    }

    return (
      <div className="grave-grid-proper">
        <div className="grid-header">
          <div className="column-labels">
            {garden.toUpperCase() === 'B' ? (
              // Garden B: Show columns 3-63
              Array.from({ length: gravesPerRow }, (_, i) => (
                <div key={i + 1} className="column-label">{config.startColumn + i}</div>
              ))
            ) : garden.toUpperCase() === 'C' ? (
              // Garden C: Show columns 2-43
              Array.from({ length: gravesPerRow }, (_, i) => (
                <div key={i + 1} className="column-label">{config.startColumn + i}</div>
              ))
            ) : garden.toUpperCase() === 'D' ? (
              // Garden D: Show columns 4-53
              Array.from({ length: gravesPerRow }, (_, i) => (
                <div key={i + 1} className="column-label">{config.startColumn + i}</div>
              ))
            ) : (
              // Garden A and others: Show columns 1-42
              Array.from({ length: gravesPerRow }, (_, i) => (
                <div key={i + 1} className="column-label">{i + 1}</div>
              ))
            )}
          </div>
        </div>
        
        <div className="grid-body">
          {sortedGraves.map((rowGraves, rowIndex) => {
            if (garden.toUpperCase() === 'B') {
              const actualRowNumber = rowGraves[0]?.row || (rowIndex + 1);
              return (
                <div key={rowIndex} className="grid-row">
                  <div className="row-label">{actualRowNumber}</div>
                  <div className="row-graves">
                    {Array.from({ length: gravesPerRow }, (_, colIndex) => {
                      const targetColumn = config.startColumn + colIndex;
                      const grave = rowGraves.find(g => g.column === targetColumn);
                      const isSelected = grave ? selectedGraves.has(`${grave.row}-${grave.column}`) : false;
                      
                      return (
                        <div
                          key={colIndex}
                          className={`grave-cell ${grave ? `grave-${grave.status}` : 'grave-empty'} ${isSelected ? 'selected' : ''}`}
                          onClick={() => grave ? handleGraveClick(grave.row, grave.column) : null}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            if (grave) {
                              // Right-click to cycle status
                              const statuses = ['available', 'occupied', 'reserved', 'maintenance', 'unavailable'];
                              const currentIndex = statuses.indexOf(grave.status);
                              const nextStatus = statuses[(currentIndex + 1) % statuses.length];
                              onStatusChange(grave.id, nextStatus);
                            }
                          }}
                          title={grave ? `${grave.name || 'Unnamed'} - ${grave.status} (Click to edit, Right-click to cycle status)` : 'Empty'}
                        >
                          {grave && (
                            <div className="grave-info">
                              <div className="grave-name">{grave.name || 'Unnamed'}</div>
                              <div className="grave-dates">
                                {grave.birth && <span>{grave.birth}</span>}
                                {grave.birth && grave.death && <span> - </span>}
                                {grave.death && <span>{grave.death}</span>}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            } else if (garden.toUpperCase() === 'C') {
              const actualRowNumber = rowGraves[0]?.row || (rowIndex + 1);
              return (
                <div key={rowIndex} className="grid-row">
                  <div className="row-label">{actualRowNumber}</div>
                  <div className="row-graves">
                    {Array.from({ length: gravesPerRow }, (_, colIndex) => {
                      const targetColumn = config.startColumn + colIndex;
                      const grave = rowGraves.find(g => g.column === targetColumn);
                      const isSelected = grave ? selectedGraves.has(`${grave.row}-${grave.column}`) : false;
                      
                      return (
                        <div
                          key={colIndex}
                          className={`grave-cell ${grave ? `grave-${grave.status}` : 'grave-empty'} ${isSelected ? 'selected' : ''}`}
                          onClick={() => grave ? handleGraveClick(grave.row, grave.column) : null}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            if (grave) {
                              // Right-click to cycle status
                              const statuses = ['available', 'occupied', 'reserved', 'maintenance', 'unavailable'];
                              const currentIndex = statuses.indexOf(grave.status);
                              const nextStatus = statuses[(currentIndex + 1) % statuses.length];
                              onStatusChange(grave.id, nextStatus);
                            }
                          }}
                          title={grave ? `${grave.name || 'Unnamed'} - ${grave.status} (Click to edit, Right-click to cycle status)` : 'Empty'}
                        >
                          {grave && (
                            <div className="grave-info">
                              <div className="grave-name">{grave.name || 'Unnamed'}</div>
                              <div className="grave-dates">
                                {grave.birth && <span>{grave.birth}</span>}
                                {grave.birth && grave.death && <span> - </span>}
                                {grave.death && <span>{grave.death}</span>}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            } else if (garden.toUpperCase() === 'D') {
              const actualRowNumber = rowGraves[0]?.row || (rowIndex + 1);
              return (
                <div key={rowIndex} className="grid-row">
                  <div className="row-label">{actualRowNumber}</div>
                  <div className="row-graves">
                    {Array.from({ length: gravesPerRow }, (_, colIndex) => {
                      const targetColumn = config.startColumn + colIndex;
                      const grave = rowGraves.find(g => g.column === targetColumn);
                      const isSelected = grave ? selectedGraves.has(`${grave.row}-${grave.column}`) : false;
                      
                      return (
                        <div
                          key={colIndex}
                          className={`grave-cell ${grave ? `grave-${grave.status}` : 'grave-empty'} ${isSelected ? 'selected' : ''}`}
                          onClick={() => grave ? handleGraveClick(grave.row, grave.column) : null}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            if (grave) {
                              // Right-click to cycle status
                              const statuses = ['available', 'occupied', 'reserved', 'maintenance', 'unavailable'];
                              const currentIndex = statuses.indexOf(grave.status);
                              const nextStatus = statuses[(currentIndex + 1) % statuses.length];
                              onStatusChange(grave.id, nextStatus);
                            }
                          }}
                          title={grave ? `${grave.name || 'Unnamed'} - ${grave.status} (Click to edit, Right-click to cycle status)` : 'Empty'}
                        >
                          {grave && (
                            <div className="grave-info">
                              <div className="grave-name">{grave.name || 'Unnamed'}</div>
                              <div className="grave-dates">
                                {grave.birth && <span>{grave.birth}</span>}
                                {grave.birth && grave.death && <span> - </span>}
                                {grave.death && <span>{grave.death}</span>}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            } else {
              // Garden A and others: Horizontal flow layout
              const rowNumber = rowIndex + 1;
              return (
                <div key={rowIndex} className="grid-row">
                  <div className="row-label">{rowNumber}</div>
                  <div className="row-graves">
                    {Array.from({ length: gravesPerRow }, (_, colIndex) => {
                      const columnNumber = colIndex + 1;
                      const grave = rowGraves[colIndex];
                      const isSelected = grave ? selectedGraves.has(`${grave.row}-${grave.column}`) : false;
                      
                      return (
                        <div
                          key={colIndex}
                          className={`grave-cell ${grave ? `grave-${grave.status}` : 'grave-empty'} ${isSelected ? 'selected' : ''}`}
                          onClick={() => grave ? handleGraveClick(grave.row, grave.column) : null}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            if (grave) {
                              // Right-click to cycle status
                              const statuses = ['available', 'occupied', 'reserved', 'maintenance', 'unavailable'];
                              const currentIndex = statuses.indexOf(grave.status);
                              const nextStatus = statuses[(currentIndex + 1) % statuses.length];
                              onStatusChange(grave.id, nextStatus);
                            }
                          }}
                          title={grave ? `${grave.name || 'Unnamed'} - ${grave.status} (Click to edit, Right-click to cycle status)` : 'Empty'}
                        >
                          {grave && (
                            <div className="grave-info">
                              <div className="grave-name">{grave.name || 'Unnamed'}</div>
                              <div className="grave-dates">
                                {grave.birth && <span>{grave.birth}</span>}
                                {grave.birth && grave.death && <span> - </span>}
                                {grave.death && <span>{grave.death}</span>}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading-spinner">Loading grave management...</div>;
  }

  return (
    <div className="grave-management">
      <div className="grave-management-header">
        <h1>Grave Management System</h1>
        <p>Manage graves across all gardens with row/column organization</p>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Garden Selection Filter */}
      <div className="garden-selector">
        <div className="garden-selector-header">
          <label htmlFor="garden-select" className="filter-label">
            <FaFilter /> Select Garden:
          </label>
          <select 
            id="garden-select"
            className="filter-select"
            value={selectedGarden}
            onChange={(e) => handleGardenSelect(e.target.value)}
          >
            {Object.keys(gardens).map(garden => (
              <option key={garden} value={garden}>
                Garden {garden}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Grave Grid */}
      <div className="grave-grid-container">
        <GraveGrid
          garden={selectedGarden}
          graves={filteredGraves}
          onStatusChange={handleGraveStatusChange}
          onGraveEdit={handleGraveEdit}
          loading={loading}
        />
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="booking-modal-overlay">
          <div className="booking-modal-content">
            <div className="booking-modal-icon">⚱️</div>
            <h3>Edit Grave Details</h3>
            <p>Update the grave information below</p>
            <form onSubmit={handleEditSubmit} noValidate>
              <div className="user-field-group">
                <input
                  type="text"
                  placeholder=" "
                  value={editForm.id}
                  readOnly
                  className="readonly-field"
                />
                <label>Lot ID</label>
                <small className="user-error"> </small>
              </div>

              <div className="user-field-group">
                <input
                  type="text"
                  placeholder=" "
                  value={editForm.name}
                  onChange={(e) => handleEditFormChange('name', e.target.value)}
                />
                <label>Name</label>
                <small className="user-error"> </small>
              </div>

              <div className="user-field-group">
                <input
                  type="number"
                  placeholder=" "
                  value={editForm.sqm}
                  onChange={(e) => handleEditFormChange('sqm', e.target.value)}
                  step="0.01"
                  min="2"
                />
                <label>Square Meters</label>
                <small className="user-error"> </small>
              </div>

              <div className="user-field-group">
                <input
                  type="number"
                  placeholder=" "
                  value={editForm.price}
                  onChange={(e) => handleEditFormChange('price', e.target.value)}
                  step="0.01"
                  min="0"
                  className="auto-calculated"
                />
                <label>Price</label>
                <small className="user-error"> </small>
              </div>

              <div className="user-field-group">
                <input
                  type="date"
                  placeholder=" "
                  value={editForm.birth}
                  onChange={(e) => handleEditFormChange('birth', e.target.value)}
                />
                <label>Birth Date</label>
                <small className="user-error"> </small>
              </div>

              <div className="user-field-group">
                <input
                  type="date"
                  placeholder=" "
                  value={editForm.death}
                  onChange={(e) => handleEditFormChange('death', e.target.value)}
                />
                <label>Death Date</label>
                <small className="user-error"> </small>
              </div>

              <div className="user-field-group">
                <select
                  value={editForm.status}
                  onChange={(e) => handleEditFormChange('status', e.target.value)}
                  className={editForm.status ? 'has-value' : ''}
                >
                  <option value="available">Available</option>
                  <option value="occupied">Occupied</option>
                  <option value="reserved">Reserved</option>
                  <option value="unavailable">Unavailable</option>
                </select>
                <label className={editForm.status ? 'lifted' : ''}>Status</label>
                <small className="user-error"> </small>
              </div>

              <div className="booking-modal-actions">
                <button type="submit" className="booking-btn-modal">
                  Update Grave
                </button>
                <button 
                  type="button" 
                  onClick={handleCloseModal} 
                  className="booking-btn-modal secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}